/**
 * 매뉴얼 문구 — `landing` 네임스페이스의 `site.manual` 로 합쳐진다(service-manual: 매뉴얼 문구의 출처).
 * 꾸밈: `<ui>…</ui>` 화면의 단추·칸 이름, `<a href="#t-…">…</a>` 다른 절로 가는 링크. 절 구조·안내 화면 좌표는
 * `Home/Manual/manualTopics.ts`·`manualGuides.ts` 에 있다.
 */
const landingManual = {
  "eyebrow": "マニュアル",
  "title": "動画で覚えるDroneSquare",
  "searchLabel": "マニュアル検索",
  "searchPlaceholder": "探したい機能を入力してください(例: マッピング、横断面)",
  "searchEmpty": "該当する機能が見つかりません。別の言葉で検索してみてください。",
  "searchCount": "{{count}}件の項目が見つかりました。",
  "chaptersLabel": "機能別ガイド",
  "jumpLabel": "マニュアルの章へ移動",
  "tocLabel": "{{chapter}}の項目一覧",
  "allChapters": "全体の目次",
  "backToStart": "最初に戻る",
  "gate": {
    "title": "マニュアルとデモ動画はログイン後にご覧いただけます",
    "text": "DroneSquare アカウントでログインするか、アカウントがない場合は 1:1 お問い合わせからご連絡ください。",
    "login": "ログイン",
    "inquiry": "1:1 お問い合わせ"
  },
  "noteLabel": {
    "note": "参考",
    "caution": "注意"
  },
  "player": {
    "label": "ステップ別ガイド: {{title}}",
    "stepsLabel": "ステップへ移動",
    "step": "ステップ{{n}}: {{title}}",
    "prev": "前のステップ: {{title}}",
    "next": "次のステップ: {{title}}",
    "play": "再生",
    "pause": "一時停止"
  },
  "chapters": {
    "c1": {
      "name": "プロジェクト",
      "subtitle": "プロジェクトと撮影日の管理"
    },
    "c2": {
      "name": "画像・マッピング",
      "subtitle": "写真をアップロードして地図にする"
    },
    "c3": {
      "name": "参考・レイヤーデータ",
      "subtitle": "図面や資料を一緒にアップロード"
    },
    "c4": {
      "name": "2D点検",
      "subtitle": "オルソ画像で現場を点検する",
      "open": "2D点検は、処理が完了した撮影日の下位テーブルのメニューで<ui>時系列点検</ui>を押すか、左サイドバーの<ui>2d3d</ui>を押すと開きます。"
    },
    "c5": {
      "name": "3D点検",
      "subtitle": "3Dモデルで現場を点検する",
      "open": "3D点検は、サイドバーの<ui>3D</ui>を押すと同じ撮影日の3Dモデルで開きます。3Dがまだ作成されていない場合は「3D(メッシュ)はまだ構築されていません」と表示されます。"
    },
    "c6": {
      "name": "断面グラフ",
      "subtitle": "縦断面と横断面を見る"
    },
    "c7": {
      "name": "作業状況管制",
      "subtitle": "作業員と機材を地図で管制する",
      "open": "作業状況管制は、プロジェクト一覧のサイドバーの<ui>worksite</ui>から入ります。2Dまたは3Dのどちらかの処理が完了した撮影日がある場合に開けます。"
    },
    "c8": {
      "name": "土工",
      "subtitle": "区域と線形で土工量を算出する",
      "open": "土工は、プロジェクトに団地図面または線形レイヤーがあり、処理が完了した撮影日がある場合に、サイドバーの<ui>earthwork</ui>から入ります。"
    }
  },
  "topics": {
    "t-new": {
      "title": "新しいプロジェクトを作成",
      "note": "一覧の「フィルター」はタグだけで絞り込みます。後でまとめて探したい区分はタグとして付けておきましょう。ウィンドウを閉じると入力内容は消えます。位置・名前・タイプ・タグ・メモは作成後も<a href=\"#t-edit\">プロジェクト情報の編集</a>で変更できます。",
      "steps": [
        {
          "title": "「新規プロジェクト作成」を押す",
          "caption": "右上の「新規プロジェクト作成」を押します。"
        },
        {
          "title": "位置のステップを始める",
          "caption": "「プロジェクト追加」ウィンドウが開きます。ステップ1「位置」: 住所を検索するか、地図をクリックします。"
        },
        {
          "title": "現場の住所を選択",
          "caption": "検索結果が表示されたら、現場の住所を選択します。"
        },
        {
          "title": "位置を確認して「次へ」",
          "caption": "地図がその位置へ移動し、下のカードに住所と緯度・経度が表示されます。「次へ」を押します。"
        },
        {
          "title": "プロジェクト情報を入力",
          "caption": "ステップ2「プロジェクト情報」: プロジェクト名、インフラタイプ、オブジェクトタイプ、タグ、メモを入力します。"
        },
        {
          "title": "「インフラタイプ」を開く",
          "caption": "プロジェクト名を入力しました。「インフラタイプ」を選択します。"
        },
        {
          "title": "オブジェクトの種類を選択",
          "caption": "一般・コリドー・面・垂直オブジェクトの中から選択します。「その他」は直接入力します。"
        },
        {
          "title": "「オブジェクトタイプ」を入力",
          "caption": "「オブジェクトタイプ」を入力します。"
        },
        {
          "title": "タグ・メモを入力して「次へ」",
          "caption": "タグ・メモは任意です。「次へ」を押します。"
        },
        {
          "title": "撮影日の欄を開く",
          "caption": "ステップ3「時系列追加」: 撮影日の欄を押して、最初の撮影日を選択します。"
        },
        {
          "title": "カレンダーで日付を選択",
          "caption": "カレンダーで日付を選択します。"
        },
        {
          "title": "「追加」で作成",
          "caption": "撮影日が入力されました。「追加」を押すとプロジェクトが作成されます。"
        },
        {
          "title": "作成完了を確認",
          "caption": "「プロジェクト作成完了」の通知が表示され、一覧に戻ります。"
        }
      ]
    },
    "t-edit": {
      "title": "プロジェクト情報の編集",
      "note": "撮影日と撮影日のメモはこのウィンドウではなく<a href=\"#t-series\">時系列の追加・修正・削除</a>で修正します。",
      "steps": [
        {
          "title": "プロジェクトを選択",
          "caption": "一覧で修正するプロジェクトを押します。"
        },
        {
          "title": "「修正」を押す",
          "caption": "右側の「情報」タブで「詳細情報」の横にある「修正」を押します。"
        },
        {
          "title": "修正する欄を直す",
          "caption": "「プロジェクト情報の編集」ウィンドウが開きます。名前・タイプ・タグ・メモのうち修正する欄を直します。"
        },
        {
          "title": "「保存」を押す",
          "caption": "修正したら「保存」を押します。「初期化」は最初の値に戻します。"
        },
        {
          "title": "修正完了を確認",
          "caption": "「プロジェクト情報が修正されました」の通知が表示され、詳細情報にすぐ反映されます。"
        }
      ]
    },
    "t-series": {
      "title": "時系列の追加・修正・削除",
      "note": "撮影日の追加・修正はプロジェクトのオーナーのみが行えます。",
      "steps": [
        {
          "title": "「時系列追加」を押す",
          "caption": "表の「時系列追加」を押します。"
        },
        {
          "title": "撮影日の欄を開く",
          "caption": "「時系列追加」ウィンドウです。撮影日の欄を押して日付を選択します。"
        },
        {
          "title": "カレンダーで撮影日を選択",
          "caption": "カレンダーで撮影日を選択します。既にある日付は作成できません。"
        },
        {
          "title": "メモを入力",
          "caption": "撮影日が入力されました。メモ欄にこの回の説明を入力します。"
        },
        {
          "title": "「完了」を押す",
          "caption": "メモを入力して「完了」を押します。"
        },
        {
          "title": "撮影日の一覧を開く",
          "caption": "新しい撮影日が追加されました。プロジェクト行の左の矢印を押すと撮影日の一覧が開きます。"
        },
        {
          "title": "修正する撮影日を選択",
          "caption": "新しい撮影日の行が表示されます。修正する撮影日を押します。"
        },
        {
          "title": "「修正」を押す",
          "caption": "右側の「情報」タブで「$t(projects:timeSeries.mappingInfo)」の横にある「修正」を押します。"
        },
        {
          "title": "メモを修正",
          "caption": "「時系列修正」ウィンドウで撮影日とメモを修正します。"
        },
        {
          "title": "「保存」を押す",
          "caption": "メモを修正しました。「保存」を押します。"
        },
        {
          "title": "ゴミ箱を押す",
          "caption": "「時系列情報が修正されました」の通知が表示されます。削除するには、その行のゴミ箱を押します。"
        },
        {
          "title": "日付を同じように入力",
          "caption": "「時系列削除」ウィンドウです。確認欄にその日付を同じように入力します。"
        },
        {
          "title": "「削除する」を押す",
          "caption": "日付が一致すると「削除する」が有効になります。削除した撮影日は元に戻せません。"
        },
        {
          "title": "削除完了を確認",
          "caption": "「削除に成功しました。」の通知が表示され、その撮影日が一覧から消えます。"
        }
      ]
    },
    "t-list": {
      "title": "プロジェクト一覧",
      "note": "「ドローン画像」タブは撮影日を選択したときだけ表示されます。",
      "steps": [
        {
          "title": "名前で検索",
          "caption": "ログインするとプロジェクト一覧が開きます。検索欄に名前の一部を入力します。"
        },
        {
          "title": "検索語を消す",
          "caption": "少し後に名前が一致するプロジェクトだけが残ります。検索語を消すと一覧全体に戻ります。"
        },
        {
          "title": "「フィルター」を開く",
          "caption": "一覧全体で「フィルター」を押すと、タグで絞り込めます。"
        },
        {
          "title": "タグを選ぶ",
          "caption": "探したいタグにチェックします。横の数字はそのタグが付いたプロジェクトの数です。"
        },
        {
          "title": "「適用」を押す",
          "caption": "「適用」を押します。「初期化」はチェックをすべて外します。"
        },
        {
          "title": "プロジェクトを選択",
          "caption": "そのタグが付いたプロジェクトだけが表と地図に残ります。プロジェクトを押します。"
        },
        {
          "title": "資料のタブを切り替える",
          "caption": "地図がその位置へ移動し、下に詳細情報のタブが表示されます。「レイヤーデータ」タブを押します。"
        },
        {
          "title": "タブの資料を見る",
          "caption": "タブを切り替えながらそのプロジェクトの資料を確認します。"
        }
      ]
    },
    "t-member": {
      "title": "メンバーと権限",
      "steps": [
        {
          "title": "プロフィールを開く",
          "caption": "左サイドバー下部のプロフィールを押します。"
        },
        {
          "title": "「プロジェクト権限管理」を開く",
          "caption": "マイページが開きます。「プロジェクト権限管理」を押します。"
        },
        {
          "title": "プロジェクトを選択して「メンバー追加」",
          "caption": "「プロジェクト選択」で自分が所有するプロジェクトを選択すると、メンバー一覧が表示されます。「メンバー追加」を押します。"
        },
        {
          "title": "メールアドレスを入力",
          "caption": "招待する人のメールアドレスを入力します。"
        },
        {
          "title": "「追加」を押す",
          "caption": "メールアドレスを入力して「追加」を押します。"
        },
        {
          "title": "役割を変える",
          "caption": "「ユーザーが追加されました」と表示され、リストに新しいメンバーが追加されます。役割を選びます。"
        },
        {
          "title": "「保存」を押す",
          "caption": "役割を変えると下の「保存」が有効になります。「保存」を押すと反映されます。"
        },
        {
          "title": "保存完了を確認",
          "caption": "「権限が修正されました」と表示されます。"
        }
      ]
    },
    "t-history": {
      "title": "プロジェクト履歴",
      "note": "リストを最後までスクロールすると、以前の記録をさらに読み込みます。時刻は<a href=\"#t-lang\">表示タイムゾーン</a>に従います。",
      "steps": [
        {
          "title": "プロフィールを開く",
          "caption": "プロジェクト履歴では、誰がいつ何を追加・修正・削除したかをプロジェクトごとにまとめて確認できます。左サイドバー下部のプロフィールを押します。"
        },
        {
          "title": "「プロジェクト履歴」を開く",
          "caption": "マイページが開きます。「プロジェクト履歴」を押します。"
        },
        {
          "title": "「フィルター」を押す",
          "caption": "「プロジェクト選択」で選んだプロジェクトの記録が、日付ごとに新しい順で表示されます。時系列で発生した記録には、右側に撮影日が付きます。「フィルター」を押します。"
        },
        {
          "title": "「アノテーション」を選ぶ",
          "caption": "記録の種類が表示されます。「アノテーション」を選びます。"
        },
        {
          "title": "アノテーションの記録を見る",
          "caption": "アノテーションの追加・修正・削除の記録だけが残ります。"
        }
      ]
    },
    "t-lang": {
      "title": "言語と表示タイムゾーン",
      "note": "選んだ言語はこのブラウザに保存され、次に開いてもそのままです。表示タイムゾーンは変更欄がなくブラウザのタイムゾーンに従い、<a href=\"#t-history\">プロジェクト履歴</a>の時刻もこのタイムゾーンで表示されます。",
      "steps": [
        {
          "title": "言語ボタンを押す",
          "caption": "画面の言語は韓国語・英語・日本語・クメール語から選べます。左サイドバー下部の、今の言語が書かれた「ko」を押します。"
        },
        {
          "title": "「日本語」を選ぶ",
          "caption": "4つの言語が表示されます。ここでは「日本語」を選びます。"
        },
        {
          "title": "プロフィールを開く",
          "caption": "メニューやボタンの文字が日本語に変わり、言語ボタンは「ja」になります。表示タイムゾーンを見るため、プロフィールを押します。"
        },
        {
          "title": "表示タイムゾーンを確認",
          "caption": "マイページの「アカウント情報」にある「表示タイムゾーン」は、ブラウザのタイムゾーンに合わせて自動で設定されています。"
        }
      ]
    },
    "t-upload": {
      "title": "ドローン画像のアップロード",
      "note": "ファイル名が同じ写真はフォルダーが違っても1枚しかアップロードされないため、複数回の飛行の写真は名前が重ならないようにしてください。",
      "steps": [
        {
          "title": "「ドローン画像」タブを開く",
          "caption": "プロジェクトと撮影日を選択すると、下に詳細情報が開きます。「ドローン画像」タブを押します。"
        },
        {
          "title": "「画像追加」を押す",
          "caption": "アップロードした写真は「アップロード完了リスト」に並びます。「画像追加」を押します。"
        },
        {
          "title": "「ファイル選択」で写真を選ぶ",
          "caption": ".jpg・.jpegファイルをドラッグ＆ドロップするか、「ファイル選択」で選択します。"
        },
        {
          "title": "「画像アップロード」を押す",
          "caption": "アップロード対象画像の枚数と総容量が表示されます。「画像アップロード」を押します。"
        },
        {
          "title": "アップロード完了を確認",
          "caption": "「画像アップロードが完了しました。」と表示され、「アップロード完了リスト」にアップロードした写真が表示されます。"
        }
      ]
    },
    "t-mapping": {
      "title": "マッピングリクエストとGCP",
      "steps": [
        {
          "title": "「GCPアップロード」を押す",
          "caption": "撮影日を選択し、「情報」タブの「$t(projects:timeSeries.mappingInfo)」を確認します。まず「GCPアップロード」を押します。"
        },
        {
          "title": "「$t(projects:timeSeries.gcpAutoDetect)」をオンにする",
          "caption": "GCPファイル($t(projects:timeSeries.gcpGuide.readableFormats))を選択すると、GCP行にファイル名が表示されます。"
        },
        {
          "title": "$t(projects:timeSeries.gcpAutoDetect)ガイドを開く",
          "caption": "標準マーカーを設置して撮影した場合は「$t(projects:timeSeries.gcpAutoDetect)」をオンにします。右の「?」が$t(projects:timeSeries.gcpAutoDetect)ガイドです。"
        },
        {
          "title": "ガイドを閉じる",
          "caption": "「$t(projects:timeSeries.gcpGuide.title)」で条件とマーカーの印刷用ファイルを確認します。閉じるボタンを押します。"
        },
        {
          "title": "「マッピングリクエスト」を押す",
          "caption": "「マッピングリクエスト」を押します。"
        },
        {
          "title": "「リクエスト」で確定",
          "caption": "確認ウィンドウです。リクエスト後は、画像を追加でアップロードしたり削除したりできません。「リクエスト」を押します。"
        },
        {
          "title": "リクエスト完了を確認",
          "caption": "「マッピングの依頼が完了しました。」と表示され、ボタンが「リクエスト完了」に変わります。"
        }
      ]
    },
    "t-download": {
      "title": "成果物のダウンロード",
      "note": "高さの値が必要な場合は、DSM GeoTIFFではなくCADパッケージの標高点を使ってください。",
      "steps": [
        {
          "title": "「生成」を押す",
          "caption": "処理が完了した撮影日の「$t(projects:timeSeries.mappingInfo)」の右にある「ダウンロード」ボックスです。解像度ごとに「生成」または「ダウンロード」があります。"
        },
        {
          "title": "「ダウンロード」を押す",
          "caption": "「生成」を押すと、「生成中 N%」が終わった後に「ダウンロード」に変わります。"
        },
        {
          "title": "ダウンロードの確認で承認",
          "caption": "1 GBを超える大きなファイルは、「ダウンロードの確認」ウィンドウが追加で表示されます。"
        }
      ]
    },
    "t-layerdata": {
      "title": "レイヤーデータ",
      "note": "アップロードしたレイヤーは、2D・3D画面の<ui>LAYER</ui>パネルでオン・オフします。",
      "steps": [
        {
          "title": "「レイヤーデータ」タブを開く",
          "caption": "プロジェクトを選択し、「レイヤーデータ」タブを押します。"
        },
        {
          "title": "アップロードアイコンを押す",
          "caption": "GIS・線形Layer・BIM・Point Cloudのボックスがあります。ボックスのヘッダーにあるアップロードアイコンを押します。"
        },
        {
          "title": "アップロード完了を確認",
          "caption": "右上にアップロード完了の通知が表示され、GISボックスにファイルが追加されます。"
        }
      ]
    },
    "t-terrain": {
      "title": "地形高度データ",
      "note": "横断数量表は、原地盤高と計画高の両方がある場合にのみ確認できます。",
      "steps": [
        {
          "title": "「地形高度データ」タブを開く",
          "caption": "プロジェクトを選択し、「地形高度データ」タブを押します。"
        },
        {
          "title": "アップロードアイコンを押す",
          "caption": "「設計図」ボックスがあります。ボックスのヘッダーにあるアップロードアイコンを押します。"
        },
        {
          "title": "整列 DXF を選択",
          "caption": "「設計図アップロード」ウィンドウで、図面ごとに整列用の DXF を組み合わせて「アップロード」します。"
        },
        {
          "title": "「アップロード」を押す",
          "caption": "整列用の DXF を選択すると、図面の横にファイル名が表示され、「アップロード」が有効になります。"
        },
        {
          "title": "アップロード完了を確認",
          "caption": "右上にアップロード完了の通知が表示され、「設計図」ボックスに図面が追加されます。"
        }
      ]
    },
    "t-ref": {
      "title": "参考データ",
      "steps": [
        {
          "title": "「参考データ」タブを開く",
          "caption": "プロジェクトを選択し、「参考データ」タブを押します。"
        },
        {
          "title": "文書のアップロードアイコンを押す",
          "caption": "画像・文書・ビデオのボックスがあります。「文書」ボックスのヘッダーにあるアップロードアイコンを押します。"
        },
        {
          "title": "アップロード完了を確認",
          "caption": "右上にアップロード完了の通知が表示され、「文書」ボックスにファイルが追加されます。"
        }
      ]
    },
    "t-2dlayer": {
      "title": "レイヤーのオン・オフ",
      "note": "GIS・線形レイヤーは<a href=\"#t-layerdata\">レイヤーデータ</a>でアップロードします。",
      "steps": [
        {
          "title": "「LAYER」を開く",
          "caption": "2D点検画面の左レールで「LAYER」を押します。"
        },
        {
          "title": "「アノテーション」をオフにする",
          "caption": "「LAYER」パネルが開きます。一番上の「アノテーション」スイッチで、地図上のイシュー表示をオン・オフします。"
        },
        {
          "title": "「GISレイヤー」を展開",
          "caption": "イシュー表示がオフになりました。「GISレイヤー」を展開します。"
        },
        {
          "title": "地籍図をオンにする",
          "caption": "アップロードしたGISレイヤーが表示されます。地籍図のスイッチをオンにします。"
        },
        {
          "title": "「線形レイヤー」を展開",
          "caption": "地籍の境界が地図に重なります。「線形レイヤー」を展開します。"
        },
        {
          "title": "計画路線をオンにする",
          "caption": "計画路線のスイッチをオンにします。"
        },
        {
          "title": "描かれた路線を見る",
          "caption": "計画路線が地図に描かれます。"
        },
        {
          "title": "「DSM」をオンにする",
          "caption": "「メインレイヤー」の「DSM」をオンにします。"
        },
        {
          "title": "高度の色を見る",
          "caption": "オルソモザイク画像の代わりにDSMの高度の色が表示されます。オルソモザイク画像とDSMは、一度にどちらか一方だけオンになります。"
        }
      ]
    },
    "t-folder": {
      "title": "レイヤーをフォルダにまとめる",
      "note": "⌘(WindowsはCtrl)を押しながら押すと、離れたレイヤーも1つずつ追加で選べます。線形レイヤーや3D画面のモデル・レイヤー一覧も同じ方法でまとめられ、フォルダの作成・移動ができるのは<a href=\"#t-member\">オーナー・編集者</a>だけです。",
      "steps": [
        {
          "title": "「LAYER」を開く",
          "caption": "複数のGISレイヤーを1つのフォルダにまとめると、一覧が短くなり、フォルダごとにオン・オフできます。2D点検画面の左レールで「LAYER」を押します。"
        },
        {
          "title": "「GISレイヤー」を展開",
          "caption": "「LAYER」パネルが開きます。「GISレイヤー」を展開します。"
        },
        {
          "title": "新規フォルダボタンを押す",
          "caption": "アップロードしたGISレイヤーが表示されます。検索欄の横にある新規フォルダボタンを押します。"
        },
        {
          "title": "フォルダ名を入力",
          "caption": "一覧の一番上にフォルダ名の欄が表示されます。名前を入力してEnterを押します。"
        },
        {
          "title": "Shiftを押しながら最初のレイヤーを押す",
          "caption": "「지하 시설물」(地下施設)フォルダができました。次に、まとめるレイヤーを選びます。Shiftを押しながら上水道管のレイヤーを押します。"
        },
        {
          "title": "Shiftを押しながら最後のレイヤーを押す",
          "caption": "上水道管が選ばれ、一覧の上に選択数が表示されます。Shiftを押しながらガス管のレイヤーを押します。"
        },
        {
          "title": "フォルダへドラッグ",
          "caption": "上水道管からガス管までの間のレイヤーがすべて選ばれました。そのうち1つをつかみ、「지하 시설물」フォルダの上にドラッグします。"
        },
        {
          "title": "フォルダを展開",
          "caption": "選んだレイヤーがまとめてフォルダに入り、一覧が短くなりました。フォルダの前の矢印を押して展開します。"
        },
        {
          "title": "フォルダのスイッチをオン",
          "caption": "フォルダの中に管路のレイヤーが入っています。フォルダ行のスイッチをオンにします。"
        },
        {
          "title": "地図で確認",
          "caption": "フォルダ内のレイヤーがまとめてオンになり、道路に沿って管路が地図に描かれます。"
        }
      ]
    },
    "t-compare": {
      "title": "時系列比較",
      "steps": [
        {
          "title": "「2分割表示」を押す",
          "caption": "地図左上の画面分割グループで「2分割表示」を押します。"
        },
        {
          "title": "右のビューの日付を開く",
          "caption": "画面が2つに分かれます。右のビューの上の日付を押します。"
        },
        {
          "title": "比較する日付を選択",
          "caption": "2D処理が終わった撮影日だけが表示されます。比較する日付を選びます。"
        },
        {
          "title": "「4分割表示」を押す",
          "caption": "同じ場所の掘削中と復旧後が並んで表示されます。次に「4分割表示」を押します。"
        },
        {
          "title": "右下のビューの日付を開く",
          "caption": "画面が4つに分かれます。右下のビューの日付を押します。"
        },
        {
          "title": "日付を選択",
          "caption": "このビューで見る撮影日を選びます。"
        },
        {
          "title": "ビューをドラッグする",
          "caption": "ビューごとに撮影日を選べます。1つのビューをドラッグします。"
        },
        {
          "title": "一緒に動くビューを見る",
          "caption": "すべてのビューが一緒に動きます。"
        }
      ]
    },
    "t-issue": {
      "title": "イシューの作成と編集",
      "note": "座標がわかっている場合は、<ui>ISSUE</ui> → <ui>イシュー作成</ui>で位置(x-y または経緯度)を入力して作成することもできます。",
      "steps": [
        {
          "title": "「点」ツールを選択",
          "caption": "描画ツールの「作成」で「点」を選択します。"
        },
        {
          "title": "地図に点を打つ",
          "caption": "カーソル横の案内に従って地図をクリックします。"
        },
        {
          "title": "「頂点編集」を押す",
          "caption": "「アノテーション作成完了」の通知とともに、すぐにイシューとして保存されます。「編集」の「頂点編集」を押します。"
        },
        {
          "title": "点をドラッグして移動",
          "caption": "点をドラッグして移動します。"
        },
        {
          "title": "移動した位置を確認",
          "caption": "置いた位置ですぐに保存されます。"
        }
      ]
    },
    "t-issueinfo": {
      "title": "イシューの検索とコメント",
      "steps": [
        {
          "title": "「ISSUE」を開く",
          "caption": "左レールで「ISSUE」を押します。"
        },
        {
          "title": "「フィルター」を開く",
          "caption": "イシューは種類ごとにまとめられています。「フィルター」を押します。"
        },
        {
          "title": "条件を外す",
          "caption": "最初はすべての条件が選ばれていて、押した条件が外れます。「マーカー」を押して外します。"
        },
        {
          "title": "「確認」を押す",
          "caption": "「マーカー」が外れました。「確認」を押します。"
        },
        {
          "title": "種類の見出しを展開",
          "caption": "条件に合うイシューだけが残ります。種類の見出しを押すと、そのイシューが展開されます。"
        },
        {
          "title": "「イシューへ移動」を押す",
          "caption": "行の位置アイコン(「イシューへ移動」)を押します。"
        },
        {
          "title": "パネルをスクロール",
          "caption": "地図がそのイシューへ移動し、「イシュー情報」が開きます。撮影日ごとの強度推移と情報を見て、パネルを下へスクロールします。"
        },
        {
          "title": "コメントを開く",
          "caption": "下の「コメント2件」を押します。"
        },
        {
          "title": "コメントを書く",
          "caption": "コメントウィンドウです。写真付きのコメントもここで見られます。入力欄にコメントを書きます。"
        },
        {
          "title": "送信ボタンを押す",
          "caption": "送信ボタンを押します。"
        },
        {
          "title": "付いたコメントを確認",
          "caption": "コメントがすぐに付きます。"
        }
      ]
    },
    "t-measure": {
      "title": "イシューの測定",
      "note": "各項目のタイトル横にあるダウンロードアイコンで、測定・体積・座標の値をCSVファイルで保存できます。",
      "steps": [
        {
          "title": "面のイシューを押す",
          "caption": "地図で面のイシューを押します。"
        },
        {
          "title": "「測定」タブを押す",
          "caption": "「イシュー情報」が開きます。「測定」タブを押します。"
        },
        {
          "title": "日付欄を開く",
          "caption": "面のイシューは水平面積・3D面積・表面積が表示されます。上の日付欄を押します。"
        },
        {
          "title": "比較する日付を選択",
          "caption": "このイシューがある撮影日が表示されます。比較する日付を選びます。"
        },
        {
          "title": "パネルをスクロール",
          "caption": "選んだ日付ごとに測定カードが付き、下の比較カードに差が表示されます。パネルを下へスクロールします。"
        },
        {
          "title": "床面高さを入力",
          "caption": "体積は床面の高さを入力して求めます。"
        },
        {
          "title": "「体積計算」を押す",
          "caption": "「体積計算」を押します。"
        },
        {
          "title": "日付ごとの体積を見る",
          "caption": "日付ごとに、基準面より上はCUT(切土量)、下はFILL(盛土量)として表示されます。"
        },
        {
          "title": "線のイシューを押す",
          "caption": "次に地図で線のイシューを押します。"
        },
        {
          "title": "「測定」タブを押す",
          "caption": "「測定」タブを押します。"
        },
        {
          "title": "パネルをスクロール",
          "caption": "線のイシューは水平距離・3D距離・表面距離が表示されます。パネルを下へスクロールします。"
        },
        {
          "title": "「線分」を展開",
          "caption": "一番下の「線分」を展開します。"
        },
        {
          "title": "線分ごとの値を見る",
          "caption": "線分ごとに距離・高度差・勾配が表示されます。"
        }
      ]
    },
    "t-issueexport": {
      "title": "イシューのエクスポート",
      "note": "右の<ui>選択時系列</ui>から日付を外して<ui>確認</ui>を押すと、その撮影日のイシューは削除されます。",
      "steps": [
        {
          "title": "イシューを押す",
          "caption": "ほかの撮影日でも追いたいイシューを押します。"
        },
        {
          "title": "パネルをスクロール",
          "caption": "「イシュー情報」が開きます。強度推移の撮影日はまだ1つだけです。パネルを下へスクロールします。"
        },
        {
          "title": "「イシューエクスポート」を押す",
          "caption": "下の「イシューエクスポート」を押します。"
        },
        {
          "title": "撮影日を選択",
          "caption": "「分析時系列選択」ウィンドウです。左の「時系列リスト」で、エクスポート先の撮影日を選びます。"
        },
        {
          "title": "「>」で移動",
          "caption": "中央の「>」で右の「選択時系列」へ移します。「»」は基準日より前・後の日付をまとめて移します。"
        },
        {
          "title": "「確認」を押す",
          "caption": "「確認」を押します。"
        },
        {
          "title": "完了通知を確認",
          "caption": "「イシュー連携成功」の通知が表示されます。"
        },
        {
          "title": "増えた強度推移を見る",
          "caption": "選んだ撮影日が「イシュー強度推移」に加わり、同じイシューを日付ごとに追えます。"
        }
      ]
    },
    "t-droneimg": {
      "title": "ドローンの元写真を見る",
      "steps": [
        {
          "title": "「ドローン画像」ツールを選択",
          "caption": "描画ツール「その他」の「ドローン画像」(カメラアイコン)を押します。"
        },
        {
          "title": "地図をクリック",
          "caption": "写真を見たい場所を地図でクリックします。"
        },
        {
          "title": "別のサムネイルを選択",
          "caption": "下の「画像リスト」に、その場所から100m以内で撮影した元写真が表示されます。別のサムネイルを押します。"
        },
        {
          "title": "全画面を押す",
          "caption": "カードがその写真に変わり、地図に撮影位置が表示されます。カードの全画面ボタンを押します。"
        },
        {
          "title": "隣の写真へ進む",
          "caption": "写真が画面いっぱいに開きます。下の矢印で隣の写真へ進みます。"
        },
        {
          "title": "隣の写真を見る",
          "caption": "隣の写真に切り替わります。"
        }
      ]
    },
    "t-geolog": {
      "title": "写真・動画(ジオログ)",
      "steps": [
        {
          "title": "「GEOLOG」を開く",
          "caption": "左レールで「GEOLOG」を押します。"
        },
        {
          "title": "「+」を押す",
          "caption": "現場の写真・動画の一覧です。上の「+」を押します。"
        },
        {
          "title": "「ファイルをアップロード」を選択",
          "caption": "「ファイルをアップロード」「フォルダをアップロード」「地図上にアップロード」から「ファイルをアップロード」を選びます。"
        },
        {
          "title": "「位置設定」を押す",
          "caption": "選んだ写真がすぐにアップロードされます。位置のない写真は「位置なし」と表示され、地図にピンがありません。「位置設定」を押します。"
        },
        {
          "title": "「位置を指定」を選択",
          "caption": "「位置を指定」を選びます。"
        },
        {
          "title": "写真を選択",
          "caption": "位置を決める写真を選びます。"
        },
        {
          "title": "「地図に指定」を押す",
          "caption": "「地図に指定」を押します。"
        },
        {
          "title": "撮影した場所をクリック",
          "caption": "地図で写真を撮った場所をクリックします。"
        },
        {
          "title": "「詳細表示」を押す",
          "caption": "その場所に写真のピンとプレビューカードが現れます。カードの「詳細表示」を押します。"
        },
        {
          "title": "大きなウィンドウで見る",
          "caption": "大きなウィンドウで写真を大きく表示し、登録者・撮影日・主題を確認します。"
        }
      ]
    },
    "t-report": {
      "title": "レポート",
      "note": "レポートには、<ui>ISSUE</ui>のフィルターに関係なく、いまの撮影日のイシューがすべて入ります。",
      "steps": [
        {
          "title": "「REPORT」を開く",
          "caption": "左レールで「REPORT」を押します。"
        },
        {
          "title": "タイトルを入力",
          "caption": "作成者の情報は自分の情報で入力済みです。レポートのタイトルを入力します。"
        },
        {
          "title": "「レポートエクスポート」を押す",
          "caption": "「レポートエクスポート」を押します。"
        },
        {
          "title": "PDFダウンロードボタンを押す",
          "caption": "A4プレビューが開きます。上のPDFダウンロードボタンを押すと、PDFファイルで保存できます。"
        }
      ]
    },
    "t-capture": {
      "title": "座標の確認と範囲キャプチャ",
      "steps": [
        {
          "title": "地図をクリック",
          "caption": "座標を見たい場所を地図でクリックします。"
        },
        {
          "title": "座標系メニューを開く",
          "caption": "右下の座標バーにその場所のx・y・zが表示されます。左の座標系の欄を押します。"
        },
        {
          "title": "「Google Mercator」を選択",
          "caption": "表示する座標系を選びます。「Google Mercator」を押します。"
        },
        {
          "title": "変わった座標を確認",
          "caption": "座標が選んだ座標系に変わります。"
        },
        {
          "title": "「範囲キャプチャ」を選択",
          "caption": "次に描画ツール「その他」の「範囲キャプチャ」を押します。"
        },
        {
          "title": "範囲指定の案内を見る",
          "caption": "地図でキャプチャする範囲をドラッグするよう案内が表示されます。"
        },
        {
          "title": "ドラッグして範囲を指定",
          "caption": "地図でキャプチャする範囲をドラッグします。"
        },
        {
          "title": "出力倍率を選択",
          "caption": "地上サイズ・出力サイズ・有効GSDが表示されます。出力倍率を選びます。"
        },
        {
          "title": "PNGダウンロードボタンを押す",
          "caption": "出力サイズが4倍になります。PNGダウンロードボタンを押すと、PNGファイルで保存できます。"
        }
      ]
    },
    "t-pf-dxf": {
      "title": "DXF断面の確認 (2D)",
      "note": "線形レイヤー(DXF)は<a href=\"#t-layerdata\">レイヤーデータ</a>の線形Layerボックスにアップロードします。",
      "steps": [
        {
          "title": "「LAYER」を押す",
          "caption": "2D点検画面の左レールで「LAYER」を押します。"
        },
        {
          "title": "「線形レイヤー」を展開する",
          "caption": "レイヤーパネルです。「線形レイヤー」を展開します。"
        },
        {
          "title": "「断面グラフを開く」を押す",
          "caption": "アップロードした線形レイヤー(DXF)が表示されます。行の右にあるグラフアイコン(「断面グラフを開く」)を押します。"
        },
        {
          "title": "縦断面の「確認」を押す",
          "caption": "断面設定パネルが開き、地図に路線と測点(0+000、0+020 …)が描かれます。「縦断面」の「確認」を押します。"
        },
        {
          "title": "グラフの線の上を押す",
          "caption": "下に縦断面グラフが開きます。横軸は始点からの距離、縦軸は高さです。グラフの線の上を押します。"
        },
        {
          "title": "地図で位置を見る",
          "caption": "押した地点が地図の路線上に点で表示され、グラフの下の行にその地点の座標と高さが出ます。"
        },
        {
          "title": "「終点」に80を入れる",
          "caption": "続いて横断面を見ます。設定パネルの「横断面」の「終点」に80を入れます。"
        },
        {
          "title": "「確認」を押す",
          "caption": "幅(左右10 m)と横断間隔(20 m)はそのままにして「確認」を押します。"
        },
        {
          "title": "次の断面ボタンを押す",
          "caption": "最初の測点の横断面グラフが開き、地図に測点ごとの横断面線が描かれます(現在の測点は赤)。グラフのヘッダーにある次の断面ボタン(›)を押します。"
        },
        {
          "title": "地図で横断面線を押す",
          "caption": "次の測点(020)に移ります。地図で0+060の横断面線を押してもその測点に変わります。"
        },
        {
          "title": "「保存」を押す",
          "caption": "グラフがその測点(060)に変わります。この区間を保存しておくには「保存」を押します。"
        },
        {
          "title": "保存の通知を見る",
          "caption": "「横断面が保存されました。」が表示されます。"
        },
        {
          "title": "保存した区間カードを押す",
          "caption": "「横断面リスト」が開きます。保存した区間のカード(0 ~ 080)を押します。"
        },
        {
          "title": "測点50を入れる",
          "caption": "区間の情報と測点リストが出ます。下の欄に50を入れます。"
        },
        {
          "title": "「特別横断面追加」を押す",
          "caption": "右の「特別横断面追加」(+)を押します。"
        },
        {
          "title": "追加の通知を見る",
          "caption": "「特別横断面が追加されました。」が表示されます。"
        },
        {
          "title": "050測点を押す",
          "caption": "リストに050測点が加わります。050を押します。"
        },
        {
          "title": "050測点から見る",
          "caption": "保存した区間のグラフが050測点から開きます。"
        }
      ]
    },
    "t-pf-dxf3d": {
      "title": "DXF断面の確認 (3D)",
      "steps": [
        {
          "title": "「LAYER」を押す",
          "caption": "3D画面の左レールで「LAYER」を押します。"
        },
        {
          "title": "「線形レイヤー」を展開する",
          "caption": "3Dレイヤーパネルです。「線形レイヤー」を展開します。"
        },
        {
          "title": "「断面グラフを開く」を押す",
          "caption": "行の右にあるグラフアイコン(「断面グラフを開く」)を押します。"
        },
        {
          "title": "縦断面の「確認」を押す",
          "caption": "2D画面と同じ断面設定パネルが開きます。「縦断面」の「確認」を押します。"
        },
        {
          "title": "横断面の「確認」を押す",
          "caption": "モデル上に測点が表示され、下に縦断面グラフが開きます。「横断面」の「確認」を押します。"
        },
        {
          "title": "モデル上の横断面線を押す",
          "caption": "モデル上に測点ごとの横断面線が描かれ、最初の測点の横断面グラフが開きます。モデル上の0+040の横断面線を押します。"
        },
        {
          "title": "その測点の横断面を見る",
          "caption": "下のグラフがその測点(040)の横断面に変わります。"
        }
      ]
    },
    "t-pf-export": {
      "title": "DXF断面のエクスポート",
      "note": "エクスポートは保存した区間からだけできます。先に<a href=\"#t-pf-dxf\">DXF断面の確認 (2D)</a>で区間を保存します。",
      "steps": [
        {
          "title": "「横断面リスト」を押す",
          "caption": "保存しておいた区間の横断面をファイルにエクスポートします。「横断面」の「横断面リスト」を押します。"
        },
        {
          "title": "「エクスポート」を押す",
          "caption": "保存した区間カードの右にあるエクスポートアイコン(「エクスポート」)を押します。"
        },
        {
          "title": "「撮影日」を押す",
          "caption": "「横断面データエクスポート」ウィンドウです。上に区間・幅・間隔が出ます。「撮影日」を押して含める日付を選びます。"
        },
        {
          "title": "撮影日を選ぶ",
          "caption": "撮影日のリストです。複数の日をまとめて選べます。2024/01/19を選びます。"
        },
        {
          "title": "「作成」を押す",
          "caption": "選んだ撮影日が入りました。設計図が1つなので「地盤高」・「計画高」は最初から選ばれています。「作成」を押します。"
        },
        {
          "title": "作成の通知を見る",
          "caption": "右上に「ファイル作成が開始されました。」が表示されます。"
        },
        {
          "title": "ウィンドウを閉じる",
          "caption": "下のリストに「作成中」のファイルができます。リストはウィンドウを開き直したときに更新されます。ウィンドウを閉じます。"
        },
        {
          "title": "もう一度「エクスポート」を押す",
          "caption": "作成が終わるころに、もう一度「エクスポート」を押します。"
        },
        {
          "title": "完了したファイルを選ぶ",
          "caption": "リストが更新され、状態が「完了」に変わりました。ファイルの前のチェックボックスを押して選びます。"
        },
        {
          "title": "「ダウンロード」を押す",
          "caption": "ファイルが選ばれました。「ダウンロード」を押します。"
        },
        {
          "title": "CSV形式を選ぶ",
          "caption": "ダウンロードする形式(CSV・DXF・土工集計表・土工横断数量)を選びます。CSVを押します。"
        },
        {
          "title": "ダウンロードの通知を見る",
          "caption": "右上に「CSVファイルがダウンロードされました。」が表示されます。"
        }
      ]
    },
    "t-pf-issue": {
      "title": "線イシュー断面の確認 (2D)",
      "steps": [
        {
          "title": "線のイシューを押す",
          "caption": "2D地図で線のイシューを押します。"
        },
        {
          "title": "「測定」タブを押す",
          "caption": "「イシュー情報」が開きます。「測定」タブを押します。"
        },
        {
          "title": "「断面確認」を押す",
          "caption": "線の長さの下に断面と横断面の設定があります。断面の線分区間(P1~P3)はそのままにして「断面確認」を押します。"
        },
        {
          "title": "パネルを下へスクロールする",
          "caption": "イシューの線に沿って切った縦断面グラフが下に開きます。今度は横断面を見ます。「イシュー情報」パネルを下へスクロールします。"
        },
        {
          "title": "「横断面確認」を押す",
          "caption": "横断面の設定で線分区間・開始測点・横断間隔・幅を決め、「横断面確認」を押します。"
        },
        {
          "title": "イシュー線の横断面を見る",
          "caption": "イシューの線に沿って横断間隔ごとに横断面が開き、地図に測点ごとの横断面線が描かれます。"
        }
      ]
    },
    "t-pf-issue3d": {
      "title": "線イシュー断面の確認 (3D)",
      "steps": [
        {
          "title": "「ISSUE」を押す",
          "caption": "3D画面でも線のイシューから断面を見られます。左レールの「ISSUE」を押します。"
        },
        {
          "title": "種類の見出しを展開",
          "caption": "イシューは種類ごとにまとめられています。線のイシューがある種類を展開します。"
        },
        {
          "title": "「イシューへ移動」を押す",
          "caption": "線のイシューの行の「イシューへ移動」を押します。"
        },
        {
          "title": "右ボタンで傾ける",
          "caption": "カメラがイシューの上へ移動して真下を見下ろし、「イシュー情報」が開きます。右ボタンを押したまま上へドラッグしてモデルを傾けます。"
        },
        {
          "title": "「測定」タブを押す",
          "caption": "モデルを斜めに見ます。「測定」タブを押します。"
        },
        {
          "title": "「断面確認」を押す",
          "caption": "2D画面と同じ断面・横断面の設定があります。「断面確認」を押します。"
        },
        {
          "title": "3D画面でグラフを見る",
          "caption": "イシューの線に沿って切った縦断面グラフが下に開き、モデルの上に頂点番号(1・2・3)が表示されます。"
        }
      ]
    },
    "t-pf-3d": {
      "title": "3D断面計測ツール",
      "steps": [
        {
          "title": "「縦断計測」を押す",
          "caption": "3D点検画面では、モデルの上に直接線を描いて断面を見られます。左のツール「測定」の「縦断計測」を押します。"
        },
        {
          "title": "最初の点を打つ",
          "caption": "モデルの上を押して線の最初の点を打ちます。掘削部に沿って打ちます。"
        },
        {
          "title": "次の点を打つ",
          "caption": "次の点を打ちます。"
        },
        {
          "title": "ダブルクリックで終える",
          "caption": "最後の点はダブルクリックで打って終えます。"
        },
        {
          "title": "グラフを見る",
          "caption": "描いた線に沿って切った縦断面グラフが下に開きます。"
        }
      ]
    },
    "t-pf-view": {
      "title": "グラフ活用(1) 操作",
      "steps": [
        {
          "title": "「部分モード」をオンにする",
          "caption": "「線イシュー断面の確認 (3D)」で開いたグラフです。頂点が3つ以上の線は「部分モード」で線分を1つずつ見られます。「部分モード」をオンにします。"
        },
        {
          "title": "「次の区間」を押す",
          "caption": "最初の区間(P1–P2)だけがグラフに出ます。「次の区間」を押します。"
        },
        {
          "title": "ホイールで拡大する",
          "caption": "次の区間(P2–P3)に移ります。グラフの上でホイールを回すと、カーソルの位置を中心に拡大します。"
        },
        {
          "title": "ドラッグで移動する",
          "caption": "拡大しました。グラフをドラッグして横へ動かします。"
        },
        {
          "title": "「その他」を押す",
          "caption": "移動しました。ヘッダーに収まらないツールは「その他」にまとまっています。「その他」を押します。"
        },
        {
          "title": "「画面初期化」を押す",
          "caption": "「画面初期化」を押すと最初の範囲に戻ります。"
        },
        {
          "title": "ハンドルでグラフ欄を広げる",
          "caption": "最初の範囲に戻りました。パネル上部のハンドルを上へドラッグすると、グラフ欄が広がります。"
        },
        {
          "title": "「その他」を押す",
          "caption": "グラフ欄が広がりました。もう一度「その他」を押します。"
        },
        {
          "title": "「透明度」バーをドラッグする",
          "caption": "「透明度」バーを右へドラッグします。"
        },
        {
          "title": "「透明度」を戻す",
          "caption": "グラフパネルが透けて、後ろの3Dモデルが見えます。バーを左端までドラッグして戻します。"
        },
        {
          "title": "「ダークテーマ」を押す",
          "caption": "パネルが不透明に戻ります。同じメニューの「ダークテーマ」を押します。"
        },
        {
          "title": "「全画面」を押す",
          "caption": "暗い背景に変わります。グラフ右上の「全画面」を押すと、グラフだけを画面いっぱいに見られます。"
        },
        {
          "title": "グラフの線の上を押す",
          "caption": "グラフの線の上を押すとその地点が固定され、下の行にX・Y・Z座標が出ます。"
        },
        {
          "title": "座標系の欄を押す",
          "caption": "座標系の欄(Korea2000)を押します。"
        },
        {
          "title": "「WGS84」を選ぶ",
          "caption": "Google Mercator・WGS84・UTMから「WGS84」を選びます。"
        },
        {
          "title": "「座標をコピー」を押す",
          "caption": "経度・緯度・高さに変わります。右の「座標をコピー」を押します。"
        },
        {
          "title": "「コピーしました」を見る",
          "caption": "「コピーしました」が表示され、座標がコピーされます。"
        }
      ]
    },
    "t-pf-layers": {
      "title": "グラフ活用(2) レイヤー比較",
      "note": "「計画」グループの設計図の面は、<a href=\"#t-terrain\">地形高度データ</a>で設計図をアップロードすると表示されます。",
      "steps": [
        {
          "title": "「レイヤー」を押す",
          "caption": "横断面グラフです。左上の「レイヤー」(4)を押します。"
        },
        {
          "title": "「現況メッシュ」を展開する",
          "caption": "現況メッシュ・BIM・Point Cloud・計画・地層・ベクターのグループがあります。「現況メッシュ」を展開します。"
        },
        {
          "title": "別の撮影日をオンにする",
          "caption": "撮影日ごとにメッシュがあります。2024-01-25をオンにします。"
        },
        {
          "title": "リストを下へスクロールする",
          "caption": "2つの撮影日の断面が色を変えて重なって描かれます(01-25では掘削部がまだ覆われています)。リストを下へスクロールします。"
        },
        {
          "title": "「計画」を展開する",
          "caption": "下の方に「計画」グループがあります。「計画」を展開します。"
        },
        {
          "title": "「計画高」をオンにする",
          "caption": "設計図の面(原地盤高・計画高)があります。「計画高」をオンにします。"
        },
        {
          "title": "「原地盤高」をオンにする",
          "caption": "設計の掘削底(計画高)が描かれます。「原地盤高」もオンにします。"
        },
        {
          "title": "「オリジナル」を押す",
          "caption": "掘削前の地面(原地盤高)まで現況と一緒に見えます。「カラーモード」の「オリジナル」を押します。"
        },
        {
          "title": "実際の色で見る",
          "caption": "現況の線がモデルの実際の色で塗られます。"
        }
      ]
    },
    "t-pf-measure": {
      "title": "グラフ活用(3) 測定",
      "steps": [
        {
          "title": "「直線測定」を押す",
          "caption": "040測点の横断面で掘削部を測ります。「描画」の「直線測定」を押します。"
        },
        {
          "title": "左上の角を押す",
          "caption": "断面線の上を押して点を打ちます。掘削部の左上の角を押します。"
        },
        {
          "title": "底に点を打つ",
          "caption": "掘削底(壁のすぐ下)を押します。最後の点からカーソルまで点線と長さがついてきます。"
        },
        {
          "title": "右クリックで終える",
          "caption": "グラフを右クリックすると測定が終わります。"
        },
        {
          "title": "「点測定」を押す",
          "caption": "区間の長さ(掘削の深さ)が付き、点ごとに座標の箱が出ます。「点測定」を押します。"
        },
        {
          "title": "底の右端を押す",
          "caption": "断面線の上の一点を押すと、その点の座標が出ます。掘削底の右端を押します。"
        },
        {
          "title": "「ラベル: 固定」を押す",
          "caption": "点の座標の箱が出ます。箱が長さを隠すときは「ラベル: 固定」を押して「ホバー」に切り替えます。"
        },
        {
          "title": "「グリッド測定」を押す",
          "caption": "座標の箱は点にマウスを乗せたときだけ出て、長さだけが残ります。「グリッド測定」を押します。"
        },
        {
          "title": "最初の角を押す",
          "caption": "四角形の対角線の両端を順に押します。右の管の左上を押します。"
        },
        {
          "title": "反対の角を押す",
          "caption": "管の右下(掘削底)を押します。"
        },
        {
          "title": "「円測定」を押す",
          "caption": "管の幅(横)・高さ(縦)・対角線の長さが出ます。「円測定」を押します。"
        },
        {
          "title": "円の中心を押す",
          "caption": "先に円の中心を押します。左の管の真ん中を押します。"
        },
        {
          "title": "円の縁を押す",
          "caption": "管の上面を押します。"
        },
        {
          "title": "「軸測定」を押す",
          "caption": "半径(R)が出ます。「軸測定」を押します。"
        },
        {
          "title": "道路の下の一点を押す",
          "caption": "カーソルを通る横・縦の線が断面線と交わる点を前もって表示します。掘削部の右側、道路の下を押します。"
        },
        {
          "title": "「測定削除」を押す",
          "caption": "交わる点どうしの距離が付きます。「測定削除」を押します。"
        },
        {
          "title": "「全て削除」を押す",
          "caption": "「選択削除」は押した測定1つだけ、「全て削除」はすべてを消します。「全て削除」を押します。"
        },
        {
          "title": "測定が消えたグラフを見る",
          "caption": "グラフ上の測定がすべて消えます。"
        }
      ]
    },
    "t-pf-image": {
      "title": "グラフ活用(4) キャプチャ・抽出",
      "steps": [
        {
          "title": "「画像キャプチャ」を押す",
          "caption": "グラフを画像ファイルで保存するには「画像キャプチャ」を押します。"
        },
        {
          "title": "「PNG」を選ぶ",
          "caption": "PNG・JPG・JPEG・WEBPから形式を選びます。「PNG」を押します。"
        },
        {
          "title": "「点間隔で抽出」を押す",
          "caption": "グラフの画像がPNGファイルでダウンロードされます。高さの値をファイルで受け取るには「点間隔で抽出」を押します。"
        },
        {
          "title": "点間隔に2を入れる",
          "caption": "「データ抽出」ウィンドウが開き、グラフの線が「点間隔」ごとに打った点を結ぶ線に変わります。点間隔に2を入れます。"
        },
        {
          "title": "CSV形式を選ぶ",
          "caption": "線が2 m間隔の点を結ぶ線で描き直されます。形式(TXT・CSV・DXF)からCSVを押します。"
        },
        {
          "title": "「確認」を押す",
          "caption": "確認ウィンドウにファイルに入るレイヤーと点間隔が出ます。「確認」を押すとファイルがダウンロードされます。"
        }
      ]
    },
    "t-pf-qty": {
      "title": "グラフ活用(5) 数量表",
      "steps": [
        {
          "title": "「数量表」を押す",
          "caption": "設計図の計画高・原地盤高をオンにした横断面です。「数量表」を押します。"
        },
        {
          "title": "「Excelダウンロード」を押す",
          "caption": "「横断数量表」ウィンドウです。この測点の原地盤高・計画高・現況高と、盛土・切土の面積を計画(青)・進行(緑)で表示します。「Excelダウンロード」を押すと表がファイルでダウンロードされます。"
        }
      ]
    },
    "t-camera": {
      "title": "カメラ操作",
      "note": "ダブルクリックでその地点に近づくのは、ツールを選んでいないときだけです。右下の案内のとおり、左ボタンのドラッグは平行移動、右ボタンのドラッグは回転・チルト、ホイールはズームです。",
      "steps": [
        {
          "title": "「3D」を押す",
          "caption": "2D点検画面の左レールで「3D」を押します。"
        },
        {
          "title": "ホイールで近づく",
          "caption": "同じ撮影日の3Dモデルが真上から見た視点で開きます。ホイールを上に回して近づきます。"
        },
        {
          "title": "右ボタンで傾ける",
          "caption": "モデルに近づきました。右ボタンを押したまま上にドラッグして傾けます。"
        },
        {
          "title": "右ボタンで回す",
          "caption": "斜めに見下ろす視点になりました。右ボタンを押したまま横にドラッグすると回ります。"
        },
        {
          "title": "左ボタンで動かす",
          "caption": "向きが変わりました。左ボタンを押したままドラッグしてモデルを動かします。"
        },
        {
          "title": "「背景色変更」を押す",
          "caption": "「その他」の「背景色変更」を押します。"
        },
        {
          "title": "背景色を選ぶ",
          "caption": "色の一覧が開きます。背景色を選びます。"
        },
        {
          "title": "ダブルクリックで近づく",
          "caption": "モデルの外側の背景が選んだ色に変わります。見たい場所をダブルクリックすると、その地点に近づきます。"
        },
        {
          "title": "「TOP」を押す",
          "caption": "その地点に近づきました。右上のビューキューブの「TOP」を押します。"
        },
        {
          "title": "真上からの視点を見る",
          "caption": "真上から見下ろす視点に変わります。"
        }
      ]
    },
    "t-fly": {
      "title": "一人称飛行",
      "steps": [
        {
          "title": "「一人称視点に切替」を押す",
          "caption": "右下の「一人称視点に切替」を押します。"
        },
        {
          "title": "操作方法の1ページ目を進める",
          "caption": "「一人称視点の操作方法」ウィンドウが開きます。W・A・S・Dで移動し、連打すると速く進みます。「次へ」を押します。"
        },
        {
          "title": "操作方法の2ページ目を進める",
          "caption": "2ページ目 — Spaceで上昇し、Shiftで下降します。「次へ」を押します。"
        },
        {
          "title": "「開始」を押す",
          "caption": "3ページ目 — スクロールでズームし、右クリックで終了します。「開始」を押します。"
        },
        {
          "title": "Wで前に進む",
          "caption": "一人称視点になりました。中央に照準、右下にキーの案内が表示されます。マウスを動かすと視点が回り、Wを押し続けると前に進みます。"
        },
        {
          "title": "右クリックで終了",
          "caption": "前に進みました。右クリック(またはEsc)で一人称視点が終わります。"
        },
        {
          "title": "元の操作に戻る",
          "caption": "ドラッグとホイールで動かす元の操作に戻りました。"
        }
      ]
    },
    "t-3dlayer": {
      "title": "3Dレイヤー",
      "note": "GIS・線形・BIM・Point Cloudレイヤーは<a href=\"#t-layerdata\">レイヤーデータ</a>でアップロードします。",
      "steps": [
        {
          "title": "「LAYER」を開く",
          "caption": "左レールで「LAYER」を押します。"
        },
        {
          "title": "「DSM」をオンにする",
          "caption": "3Dレイヤーパネルです。「DSM」をオンにします。"
        },
        {
          "title": "高さの色を見る",
          "caption": "モデルが高さに応じて色分けされ、DSMの下にLowest–Highestの凡例が付きます。"
        },
        {
          "title": "「DSM」をオフにする",
          "caption": "「DSM」をオフにします。"
        },
        {
          "title": "透明度スライダーをドラッグ",
          "caption": "「Mesh」の「透明度」スライダーをドラッグして、モデルを半透明にします。"
        },
        {
          "title": "透明度を戻す",
          "caption": "モデルが半透明になり、奥が透けて見えます。スライダーを左端までドラッグして戻します。"
        },
        {
          "title": "「アノテーション」をオンにする",
          "caption": "モデルが不透明に戻りました。「アノテーション」をオンにします。"
        },
        {
          "title": "イシューを見る",
          "caption": "イシューがモデルの上に描かれます。"
        },
        {
          "title": "「線形レイヤー」を展開",
          "caption": "「線形レイヤー」を展開します。"
        },
        {
          "title": "計画路線をオンにする",
          "caption": "線形レイヤーの一覧です。計画路線をオンにします。"
        },
        {
          "title": "描かれた路線を見る",
          "caption": "計画路線がモデルの上に描かれます。"
        },
        {
          "title": "「モデルへ移動」を押す",
          "caption": "「Mesh」の右の照準ボタン(「モデルへ移動」)を押します。"
        },
        {
          "title": "モデル全体を見る",
          "caption": "モデル全体が見えるよう、真上からの視点に移動します。"
        }
      ]
    },
    "t-measure3d": {
      "title": "距離・垂直計測",
      "note": "測定は保存されず、基準の時系列を切り替えると消えます。",
      "steps": [
        {
          "title": "「距離計測」を選ぶ",
          "caption": "「測定」の「距離計測」を選びます。"
        },
        {
          "title": "最初の点を打つ",
          "caption": "モデルの上をクリックして最初の点を打ちます。クリックするたびに点が増え、ダブルクリックで終わります。"
        },
        {
          "title": "次の点を打つ",
          "caption": "次の点を打ちます。区間ごとに3D距離が付きます。"
        },
        {
          "title": "ダブルクリックで終える",
          "caption": "最後の点はダブルクリックで打って終えます。"
        },
        {
          "title": "区間の距離を見る",
          "caption": "距離計測が終わりました。区間ごとに3D距離が付いています。"
        },
        {
          "title": "「垂直計測」を選ぶ",
          "caption": "今度は掘削の深さを測ります。「測定」の「垂直計測」を選びます。"
        },
        {
          "title": "上の点を打つ",
          "caption": "掘削部の横の道路面をクリックします。"
        },
        {
          "title": "下の点を打つ",
          "caption": "掘削の底をクリックすると計測が終わります。"
        },
        {
          "title": "高さ・水平・3D距離を見る",
          "caption": "高さ(垂直)・水平距離と、2点間の3D距離が一緒に付きます。"
        }
      ]
    },
    "t-area3d": {
      "title": "面積・縦断計測",
      "note": "縦断計測のグラフは、<a href=\"#c6\">断面グラフ</a>の章のグラフと同じ画面です。",
      "steps": [
        {
          "title": "「面積計測」を選ぶ",
          "caption": "「測定」の「面積計測」を選びます。"
        },
        {
          "title": "最初の頂点を打つ",
          "caption": "モデルの上をクリックして最初の頂点を打ちます。"
        },
        {
          "title": "2つ目の頂点を打つ",
          "caption": "次の頂点を打ちます。"
        },
        {
          "title": "3つ目の頂点を打つ",
          "caption": "辺ができて距離が付きます。次の頂点を打ちます。"
        },
        {
          "title": "4つ目の頂点を打つ",
          "caption": "頂点が3つになると面積が付きます。次の頂点を打ちます。"
        },
        {
          "title": "ダブルクリックで終える",
          "caption": "最後の頂点はダブルクリックで打って終えます。"
        },
        {
          "title": "面積を見る",
          "caption": "面積計測が終わりました。中央に3D面積・平面面積、辺ごとに距離が付きます。"
        },
        {
          "title": "「縦断計測」を選ぶ",
          "caption": "「縦断計測」を選びます。"
        },
        {
          "title": "始点を打つ",
          "caption": "線の始点をクリックします。"
        },
        {
          "title": "終点をダブルクリック",
          "caption": "終点をダブルクリックします。"
        },
        {
          "title": "高さのグラフを見る",
          "caption": "描いた線に沿って測った高さのグラフが下に開きます。掘削部と配管が断面で見えます。"
        },
        {
          "title": "「計測消しゴム」を選ぶ",
          "caption": "「計測消しゴム」を選びます。"
        },
        {
          "title": "消す計測を押す",
          "caption": "消す計測(面積)をクリックします。"
        },
        {
          "title": "消えた場所を見る",
          "caption": "面積計測が消えました。計測は保存されないため、画面を開き直すと消えます。"
        }
      ]
    },
    "t-issue3d": {
      "title": "3Dイシュー",
      "note": "線・四角形・面・文字も「作成」から作れ、点・文字は1回目のクリック、四角形は2回目のクリック、線・面はダブルクリックで終わります。現在の撮影日にないイシューで「イシューへ移動」を押すと、「現在の時系列にないイシュー」ウィンドウが開きます。",
      "steps": [
        {
          "title": "「点」ツールを選ぶ",
          "caption": "「作成」の「点」を選びます。"
        },
        {
          "title": "モデルの上をクリック",
          "caption": "イシューレイヤーがオフだった場合は自動でオンになり、通知が出ます。イシューを置く場所をモデルの上でクリックします。"
        },
        {
          "title": "イシュー情報を見る",
          "caption": "すぐに「課題 N」として保存され、左に「イシュー情報」が開きます。名前・種類・強度・メモは「修正」で変えます。"
        },
        {
          "title": "「ISSUE」を開く",
          "caption": "左レールで「ISSUE」を押します。"
        },
        {
          "title": "種類の見出しを展開",
          "caption": "イシューは種類ごとにまとめられています。種類の見出しを展開します。"
        },
        {
          "title": "「イシューへ移動」を押す",
          "caption": "見たいイシューの行の「イシューへ移動」を押します。"
        },
        {
          "title": "「課題名を表示」を押す",
          "caption": "カメラがそのイシューへ移動し、「イシュー情報」が開きます。「その他」の「課題名を表示」を押します。"
        },
        {
          "title": "隠れた名前を見る",
          "caption": "モデル上のイシュー名が隠れます。もう一度押すと再び表示されます。"
        }
      ]
    },
    "t-clip": {
      "title": "断面の作成",
      "note": "同時にオンにできる断面は8つまでです。一覧の鉛筆ボタンで名前を変え、マイナスボタンで削除します(削除した断面は元に戻せません)。",
      "steps": [
        {
          "title": "「CLIP」を開く",
          "caption": "左レールで「CLIP」を押します。"
        },
        {
          "title": "「一辺」を入力",
          "caption": "「断面ツール」が開き、「断面を追加」で「ボックス」が選ばれています。「一辺」にボックスの一辺の長さ(m)を入力します。"
        },
        {
          "title": "名前を入力",
          "caption": "名前を入力します。空欄にすると「ボックス 1」のように番号が付きます。"
        },
        {
          "title": "「表面クリックで追加」を押す",
          "caption": "「表面クリックで追加」を押します。"
        },
        {
          "title": "ボックスの位置をクリック",
          "caption": "「3D画面でモデルの表面をクリックしてください」と表示されます。ボックスの中心にする場所をモデルの上でクリックします。右クリックやEscで取り消します。"
        },
        {
          "title": "スイッチでオフにする",
          "caption": "ボックスの内側だけが残り、残りは隠れます。「断面リスト」にボックスが追加され、すぐに保存されます。一覧のスイッチで断面をオン・オフします。"
        },
        {
          "title": "「平面」を選ぶ",
          "caption": "断面がオフになり、モデル全体が見えます。もう一度オンにするとカメラがその断面へ移動します。今度は「平面」を選びます。"
        },
        {
          "title": "「表面クリックで追加」を押す",
          "caption": "「表面クリックで追加」を押します。"
        },
        {
          "title": "平面の位置をクリック",
          "caption": "平面が通る場所をモデルの上でクリックします。"
        },
        {
          "title": "水平な平面を見る",
          "caption": "クリックした高さに「一辺」の大きさの水平な平面ができ、その上側だけが残ります。平面を立てて垂直断面を見る方法は「断面の調整と保存」で説明します。"
        }
      ]
    },
    "t-clipedit": {
      "title": "断面の調整と保存",
      "note": "「垂直」ジョイスティックは上下に動かし、3つ目のジョイスティックはボックスなら「間隔」(高さ)、平面なら「面積」(大きさ)を変えます。",
      "steps": [
        {
          "title": "平面を展開",
          "caption": "一覧で平面を押して展開します。"
        },
        {
          "title": "「編集」を押す",
          "caption": "「エッジ」は断面の輪郭、「断面の塗り」は切り口の塗りをオン・オフします。「反転」は残す側を切り替えます。「編集」を押します。"
        },
        {
          "title": "「傾き」をドラッグ",
          "caption": "編集中は、3D画面の頂点・面を直接ドラッグするか、下のジョイスティック・スライダーで変えます。「傾き」スライダーを左端までドラッグして平面を立てます。"
        },
        {
          "title": "「水平」ジョイスティックをドラッグ",
          "caption": "平面が立ち、掘削部を横切る断面が見えます。「水平」ジョイスティックを押したままドラッグするとその方向に動き続け、離すと止まります。"
        },
        {
          "title": "移動した断面を見る",
          "caption": "断面が掘削部に沿って移動しました。「回転」スライダーでは平面の向きを回します。"
        },
        {
          "title": "「保存」を押す",
          "caption": "変更した断面は名前の横にオレンジの点が付き、「保存」がオンになります。「保存」を押します。"
        },
        {
          "title": "保存を確認",
          "caption": "保存されました。オレンジの点が消え、編集が閉じます。保存前は「リセット」で最後に保存した形に戻し、編集中は「元に戻す」・「やり直し」を使います。"
        }
      ]
    },
    "t-vector": {
      "title": "ベクター",
      "note": "緯度/経度の代わりに「X/Y 座標」(プロジェクト座標系)でも入力できます。パネル上部の「地図に表示」で、ベクターをまとめて隠したり表示したりします。",
      "steps": [
        {
          "title": "「VECTOR」を開く",
          "caption": "左レールで「VECTOR」を押します。"
        },
        {
          "title": "「座標入力で作成」を押す",
          "caption": "「ベクター」パネルです。「座標入力で作成」を押します。"
        },
        {
          "title": "名前を入力",
          "caption": "「ベクター作成」ウィンドウです。名前を入力します。"
        },
        {
          "title": "「円柱」を選ぶ",
          "caption": "形(線・円柱・四角柱)を選びます。「円柱」を押します。"
        },
        {
          "title": "半径を入力",
          "caption": "「半径(m)」を入力します。四角柱は「一辺(m)」を入力します。"
        },
        {
          "title": "座標を入力",
          "caption": "点ごとに緯度・経度・高さ(m)を入力します。高さは標高で、「点を追加」で点を増やせます(2〜100個)。"
        },
        {
          "title": "「作成」を押す",
          "caption": "「作成」を押します。"
        },
        {
          "title": "描かれた円柱を見る",
          "caption": "一覧にベクターが追加され、3D画面の掘削部に円柱が描かれます。"
        },
        {
          "title": "ベクターを展開",
          "caption": "ベクターを押して展開します。"
        },
        {
          "title": "「垂直断面」を押す",
          "caption": "「情報」で名前・形・半径・色・メモを、「座標リスト」で座標を直して「保存」します。「垂直断面」を押します。"
        },
        {
          "title": "切断線の長さを入力",
          "caption": "「垂直断面」ウィンドウです。切断位置(始点からの距離)は中央に入っています。「切断線の長さ(m)」を8 mに変えます。"
        },
        {
          "title": "「確認」を押す",
          "caption": "「確認」を押します。"
        },
        {
          "title": "断面グラフを見る",
          "caption": "切断線が描かれ、カメラがその上に移動し、下に断面グラフが開きます。モデルの断面に円柱の断面が重ねて描かれ、計画管路と実際の配管の位置を比べられます。"
        }
      ]
    },
    "t-members": {
      "title": "接続中の作業者と立入禁止区域",
      "steps": [
        {
          "title": "作業者の行をクリック",
          "caption": "作業状況の画面です。「MEMBERS」(接続中の作業者)パネルにプロジェクトの参加者が並び、モバイルアプリから位置を送っている人は地図にピンで表示されます。2行目をクリックします。"
        },
        {
          "title": "カードを確認",
          "caption": "地図がその人の位置へ移動し、カードが開きます。役割、最終位置報告、GPS精度、違反状態(いまは「正常」)が表示されます。"
        },
        {
          "title": "「ZONES」をクリック",
          "caption": "この人がいる掘削部を立入禁止区域にしてみます。左のレールの「ZONES」をクリックします。"
        },
        {
          "title": "「新しい立入禁止区域を描く」をクリック",
          "caption": "「立入禁止区域の管理」です。区域ごとにオン・オフのスイッチがあり、⋯メニューから名前の変更や削除をします。「新しい立入禁止区域を描く」をクリックします。"
        },
        {
          "title": "最初の頂点を打つ",
          "caption": "描画の案内が表示されます。掘削部の周りに最初の頂点を打ちます。"
        },
        {
          "title": "2つ目の頂点を打つ",
          "caption": "2つ目の頂点を打ちます。"
        },
        {
          "title": "3つ目の頂点を打つ",
          "caption": "3つ目の頂点を打ちます。"
        },
        {
          "title": "最後の頂点をダブルクリック",
          "caption": "最後の頂点はダブルクリックで打って描画を終えます。"
        },
        {
          "title": "区域名を入力",
          "caption": "「新しい立入禁止区域の名前」ウィンドウが開きます。区域名を入力します。"
        },
        {
          "title": "「確認」をクリック",
          "caption": "「確認」をクリックします。"
        },
        {
          "title": "違反の通知を確認",
          "caption": "区域が作られ、その中にいる作業者について違反の通知が表示されます。"
        },
        {
          "title": "新しい区域を確認",
          "caption": "一覧に区域が追加され、地図に描かれます。作業者のピンは区域の中にあります。"
        },
        {
          "title": "「MEMBERS」をクリック",
          "caption": "作業者の一覧でも確認します。「MEMBERS」をクリックします。"
        },
        {
          "title": "赤い行を確認",
          "caption": "その作業者の行が赤くなり、「違反」と区域名が付きます。開いているカードの違反状態も「違反」に変わります。"
        }
      ]
    },
    "t-equip": {
      "title": "機材・施設の配置",
      "note": "一覧の目のアイコンで地図から隠した機材は、作業半径の重なり(危険)に数えません。",
      "steps": [
        {
          "title": "「EQUIP」をクリック",
          "caption": "作業状況の画面の左のレールで「EQUIP」をクリックします。"
        },
        {
          "title": "「機材・施設を追加」をクリック",
          "caption": "「機材・施設」パネルに配置済みの機材が表示されます。「機材・施設を追加」をクリックします。"
        },
        {
          "title": "「油圧ショベル」を選ぶ",
          "caption": "地図の上に分類(揚重・高所、土工・運搬、仮設、資材、人員)と種類のカードが開きます。「油圧ショベル」をクリックします。"
        },
        {
          "title": "規格を決めて「配置」をクリック",
          "caption": "配置ウィンドウで名前、基準規格、全長・全幅・全高・作業半径、半径の色を決めて「配置」をクリックします。"
        },
        {
          "title": "置く場所をクリック",
          "caption": "種類のカードが薄くなり、配置の状態になります。地図で1台目の油圧ショベルの隣をクリックします。右クリックでキャンセルします。"
        },
        {
          "title": "重なった作業半径を確認",
          "caption": "2台目が置かれます。2台の作業半径が重なった所が赤く塗られ、一覧の2行に危険マーク(!)が付きます。"
        },
        {
          "title": "置いた機材の行を展開",
          "caption": "重ならないように動かしてみます。置いた機材の行の左にある展開ボタンをクリックします。"
        },
        {
          "title": "「位置・向き」タブをクリック",
          "caption": "「規格」「位置・向き」「動線」のタブがあります。「位置・向き」をクリックします。"
        },
        {
          "title": "「地図で移動」をクリック",
          "caption": "向きのダイヤルと経度・緯度・高さが表示されます。「地図で移動」をクリックします。"
        },
        {
          "title": "新しい場所をクリック",
          "caption": "ボタンが「移動中」に変わります。地図で作業半径が重ならない場所をクリックします。"
        },
        {
          "title": "移動した位置を確認",
          "caption": "機材がその場所へ移り、経度・緯度が変わります。作業半径が重ならなくなり、危険マークが消えます。"
        }
      ]
    },
    "t-route": {
      "title": "機材の動線シミュレーション",
      "note": "再生中に変わる重なりは地図だけに表示され、一覧の危険マークは保存した位置で数えます。",
      "steps": [
        {
          "title": "1台目の行を展開",
          "caption": "「機材・施設」パネルです。2台目の油圧ショベルにはすでに動線があります。1台目の行の左にある展開ボタンをクリックします。"
        },
        {
          "title": "「動線」タブをクリック",
          "caption": "「動線」タブをクリックします。"
        },
        {
          "title": "「動線を描く」をクリック",
          "caption": "「まだ動線がありません」と表示されます。「動線を描く」をクリックします。"
        },
        {
          "title": "次の点を打つ",
          "caption": "機材の位置が最初の地点になります(「地点」1)。地図で次の点をクリックします。"
        },
        {
          "title": "最後の点をダブルクリック",
          "caption": "最後の点はダブルクリックで打って終えます。"
        },
        {
          "title": "「動線を再生」をクリック",
          "caption": "動線が保存され、「動線シミュレーション」に再生バー・リピート・速度と地点・距離・予想時間が表示されます。再生ボタンをクリックします。"
        },
        {
          "title": "2台目の行を展開",
          "caption": "1台目が動線に沿って動きます。一緒に動かすため、2台目の行を展開します。"
        },
        {
          "title": "「動線」タブをクリック",
          "caption": "2台目の「動線」タブをクリックします。"
        },
        {
          "title": "「動線を再生」をクリック",
          "caption": "2台目の再生ボタンをクリックします。"
        },
        {
          "title": "重なる瞬間を確認",
          "caption": "2台が同時に動き、作業半径が重なると重なった所が赤く塗られ、名札に危険マーク(!)が付きます。"
        }
      ]
    },
    "t-ewzone": {
      "title": "3Dで見る区域の土工量",
      "note": "数量は区域内のすべてのセルから算出し、「計算方式」の断面図はその計算を示す例示区間です。",
      "steps": [
        {
          "title": "「＋ 区域を描く」を押す",
          "caption": "土工画面です。「3Dボクセル」では、3Dモデルの上に描いた区域にかかる図面をすべて合算して土工量を算出します。「＋ 区域を描く」を押します。"
        },
        {
          "title": "1つ目の頂点を打つ",
          "caption": "パネルに「区域を描画中です。ダブルクリックすると描画が完了します。」と表示されます。掘削部の周りに1つ目の頂点を打ちます。"
        },
        {
          "title": "2つ目の頂点を打つ",
          "caption": "2つ目の頂点を打ちます。"
        },
        {
          "title": "3つ目の頂点を打つ",
          "caption": "3つ目の頂点を打ちます。"
        },
        {
          "title": "最後の頂点をダブルクリック",
          "caption": "最後の頂点はダブルクリックで打って描画を終えます。"
        },
        {
          "title": "「計算する」を押す",
          "caption": "「区域 1」として保存されてリストに追加され、モデルの上に塗られます。「区域 1で使用された図面」に区域にかかる図面が表示されます。「土工量」カードの「計算する」を押します。"
        },
        {
          "title": "「算出済みの時系列リスト」を開く",
          "caption": "計算が終わると、カバレッジと「計算方式」・「土工集計表を見る」が表示されます。「算出済みの時系列リスト 2/2」を開きます。"
        },
        {
          "title": "「2024-01-25」のチェックを外す",
          "caption": "算出に含まれた時系列が表示されます。01-25は掘削部を埋め戻した後なので、算出から外してみます。「2024-01-25」のチェックを外します。"
        },
        {
          "title": "「再計算」を押す",
          "caption": "「算出から除外しました」が付き、算出に含まれる時系列が変わったという案内と「再計算」が表示されます。「再計算」を押します。"
        },
        {
          "title": "「計算方式」を押す",
          "caption": "リストが「1/2」に変わり、案内が消えます。「計算方式」を押します。"
        },
        {
          "title": "ウィンドウ内を下へスクロール",
          "caption": "「計算方式」ウィンドウです。例示区間の断面図で、セルごとに2つの面の差を数える方法と計画(原地盤高→計画高)の数量を示します。ウィンドウ内を下へスクロールします。"
        },
        {
          "title": "出来形・残余を確認",
          "caption": "時系列ごとに、出来形(原地盤高→ドローン地盤高)と残余(ドローン地盤高→計画高)の断面図と進捗率が表示されます。計画 = 出来形 + 残余です。"
        }
      ]
    },
    "t-ewtable": {
      "title": "土工集計表",
      "note": "区域が複数あると、区域ごとに表ができます。表をドラッグして順序を変え、「CSVエクスポートに含める」でファイルに入れる区域を選びます。",
      "steps": [
        {
          "title": "「土工集計表を見る」を押す",
          "caption": "区域 1が選ばれています。「土工量」カードの「土工集計表を見る」を押します。"
        },
        {
          "title": "「透明度」バーをドラッグする",
          "caption": "「土工集計表」ウィンドウです。切土(土砂)と盛土(路床・路体)を、計画数量・残余土工量・日別累計と撮影日ごとの進捗数量で表示します。「透明度」バーを右へドラッグします。"
        },
        {
          "title": "表の下のモデルを見る",
          "caption": "表が透けて、下の3Dモデルと区域が一緒に見えます。ウィンドウ上部をドラッグして移動でき、「CSVエクスポート (区域 1件)」で表をファイルとして受け取ります。"
        }
      ]
    },
    "t-linear": {
      "title": "線形ベースの土工",
      "note": "断面の設定・グラフ・横断数量表は<a href=\"#c6\">06 断面グラフ</a>と同じです。",
      "steps": [
        {
          "title": "「線形ベース」を押す",
          "caption": "「線形ベース」では、1本の線形で測点ごとに断面と数量を見ます。土工パネル上部の「線形ベース」を押します。"
        },
        {
          "title": "「断面グラフを開く」を押す",
          "caption": "線形レイヤーのリストと、断面・数量表をどの時系列の現況面で算出するかが表示されます。行のグラフアイコン(「断面グラフを開く」)を押します。"
        },
        {
          "title": "「横断面」の「確認」を押す",
          "caption": "モデルの上に路線と測点が表示され、断面の設定パネルが開きます。「横断面」の「確認」を押します。"
        },
        {
          "title": "路線を中央へドラッグする",
          "caption": "測点ごとに横断面の線が描かれ、最初の測点の横断面グラフが下に開きます。路線がグラフに隠れないよう、左ボタンでドラッグして中央へ移します。"
        },
        {
          "title": "右ボタンで傾ける",
          "caption": "真上から見下ろした画面です。右ボタンを押したまま上へドラッグしてモデルを傾けます。"
        },
        {
          "title": "0+060の横断面の線を押す",
          "caption": "モデルを斜めから見ます。モデル上の0+060の横断面の線を押します。"
        },
        {
          "title": "「その他」を押す",
          "caption": "グラフがその測点(060)の横断面に変わります。「その他」を押します。"
        },
        {
          "title": "「数量表」を押す",
          "caption": "「数量表」を押します。"
        },
        {
          "title": "数量表を見る",
          "caption": "「横断数量表」ウィンドウです。この測点の原地盤高・計画高・現況高と盛土・切土の面積を、計画(青)・進捗(緑)で表示します。"
        }
      ]
    },

  },
  "mapping": {
    "gcpPath": "GCP(地上基準点)を使うと位置精度が上がります。$t(projects:timeSeries.mappingInfo)のGCP行でGCPファイル($t(projects:timeSeries.gcpGuide.readableFormats))をアップロードしてから、マッピングをリクエストしてください。",
    "autoDetect": "GCP行に[$t(projects:timeSeries.gcpAutoDetect)]が表示されていれば、DroneSquare標準マーカーを設置して撮影した回は、人の手を介さず自動でマッピングされます。マーカーは撮影前に準備する必要があるため、先に下のガイドをご確認ください。",
    "ownerOnly": "マッピングのリクエストはプロジェクトのオーナーのみが行えます。",
    "processingTime": "処理時間は写真の枚数と処理方式(自動・担当者)によって異なります。"
  }
} as const;

export default landingManual;
